/**
 * useWebRTC Composable
 *
 * Comprehensive WebRTC peer-to-peer connection manager for the Canasta game.
 * Handles video/audio sharing between players with automatic connection management.
 *
 * Features:
 * - RTCPeerConnection management for each player
 * - WebRTC signaling via Socket.IO (offers, answers, ICE candidates)
 * - Remote stream handling with reactive state
 * - Automatic peer connection cleanup on disconnect
 * - STUN server configuration for NAT traversal (Google public STUN)
 * - Bot/spectator filtering (no connections to non-human players)
 * - Connection state monitoring
 *
 * Architecture:
 * - One RTCPeerConnection per remote player
 * - Initiator creates offer, responder creates answer
 * - ICE candidates exchanged after SDP negotiation
 * - Backend forwards all signaling messages
 *
 * @example
 * const { init, setLocalStream, remoteStreams, playerVideoStates } = useWebRTC()
 * init(socket, myPlayerId)  // Initialize with socket connection
 * setLocalStream(stream)    // Share local media stream
 * updatePlayers(playerIds)  // Update peer list
 * initiateConnections()     // Start peer connections
 */

import { ref, readonly, onUnmounted } from 'vue'
import { Logger, LogCategory } from '@/utils/Logger.class'
import { WebRTCManager, type VideoState } from './WebRTCManager'

// Lazy-initialize logger to avoid "Cannot access before initialization" errors
let rtcLog: ReturnType<typeof Logger.scope> | null = null
function getLogger() {
  if (!rtcLog) {
    rtcLog = Logger.scope(LogCategory.WEBRTC)
  }
  return rtcLog
}

// Re-export VideoState for consumers
export type { VideoState }

/**
 * Reactive WebRTC state (global, shared across instances)
 */
const remoteStreams = ref<Map<string, MediaStream>>(new Map())
const playerVideoStates = ref<Map<string, VideoState>>(new Map())
const connectionStates = ref<Map<string, RTCPeerConnectionState>>(new Map())
const isInitialized = ref(false)

/**
 * Singleton manager instance
 */
let managerInstance: WebRTCManager | null = null
let currentPlayerId: string | null = null

/**
 * Get or create the WebRTCManager singleton
 */
function getManager(): WebRTCManager {
  if (!managerInstance) {
    managerInstance = new WebRTCManager()

    // Setup reactive callbacks
    managerInstance.onRemoteStream = (playerId: string, stream: MediaStream) => {
      // VIDEO-16: Remote track/stream received
      console.log('[VIDEO-16] Remote stream received', {
        fromPlayer: playerId,
        streamId: stream.id,
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
        tracks: stream.getTracks().map(t => ({
          kind: t.kind,
          id: t.id,
          enabled: t.enabled,
          readyState: t.readyState
        }))
      })

      getLogger().info('Remote stream received', {
        playerId,
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
        active: stream.active
      })
      remoteStreams.value.set(playerId, stream)
      // Trigger reactivity by creating new Map
      remoteStreams.value = new Map(remoteStreams.value)

      // VIDEO-17: Stream stored in remoteStreams
      console.log('[VIDEO-17] Remote stream stored', {
        playerId,
        streamId: stream.id,
        totalRemoteStreams: remoteStreams.value.size
      })
    }

    managerInstance.onRemoteStreamEnded = (playerId: string) => {
      getLogger().info('Remote stream ended', { playerId })
      remoteStreams.value.delete(playerId)
      remoteStreams.value = new Map(remoteStreams.value)
    }

    managerInstance.onConnectionStateChange = (playerId: string, state: RTCPeerConnectionState) => {
      getLogger().info('Connection state changed', { playerId, state })
      connectionStates.value.set(playerId, state)
      connectionStates.value = new Map(connectionStates.value)
    }

    managerInstance.onVideoStateChange = (playerId: string, state: VideoState) => {
      // VIDEO-9: Received player_video_state from remote player
      console.log('[VIDEO-9] Received player_video_state', {
        fromPlayer: playerId,
        videoEnabled: state.video_enabled,
        audioEnabled: state.audio_enabled
      })

      getLogger().info('Player video state changed', { playerId, ...state })
      playerVideoStates.value.set(playerId, state)
      playerVideoStates.value = new Map(playerVideoStates.value)
    }
  }

  return managerInstance
}

/**
 * useWebRTC Composable
 *
 * Provides reactive Vue integration for the WebRTCManager.
 */
export function useWebRTC() {
  const manager = getManager()

  /**
   * Initialize WebRTC with socket and player ID
   */
  function init(socketInstance: any, playerId: string): void {
    getLogger().info('useWebRTC.init() called', { playerId })

    currentPlayerId = playerId
    manager.init(socketInstance, playerId)
    isInitialized.value = true

    getLogger().info('WebRTC initialized', { playerId, socketId: socketInstance?.id })
  }

  /**
   * Set local media stream to share with peers
   */
  function setLocalStream(stream: MediaStream | null): void {
    getLogger().info('setLocalStream() called', {
      hasStream: !!stream,
      videoTracks: stream?.getVideoTracks().length ?? 0,
      audioTracks: stream?.getAudioTracks().length ?? 0
    })

    manager.setLocalStream(stream)
  }

  /**
   * Update list of other players in room
   * SECURITY: Also sets valid participants for WebRTC peer validation
   */
  function updatePlayers(playerIds: string[]): void {
    // SECURITY: Set valid participants before updating connections
    // This prevents accepting WebRTC signals from unknown peers
    manager.setValidParticipants(playerIds)
    manager.updatePlayers(playerIds)
  }

  /**
   * Initiate WebRTC connections to all peers
   */
  function initiateConnections(): void {
    // VIDEO-10: Initiating WebRTC connections
    console.log('[VIDEO-10] Initiating WebRTC connections', {
      playerId: currentPlayerId
    })

    manager.initiateConnections()
  }

  /**
   * Notify peers of local video/audio state change
   */
  function notifyVideoStateChange(videoEnabled: boolean, audioEnabled: boolean): void {
    // VIDEO-6: Before emitting video_state_changed
    console.log('[VIDEO-6] Emitting video_state_changed', {
      videoEnabled,
      audioEnabled,
      playerId: currentPlayerId
    })

    manager.notifyVideoStateChange(videoEnabled, audioEnabled)
  }

  /**
   * Request current video states from all connected players
   */
  function requestVideoStates(): void {
    manager.requestVideoStates()
  }

  /**
   * Get remote stream for a specific player
   */
  function getRemoteStream(playerId: string): MediaStream | null {
    return remoteStreams.value.get(playerId) || null
  }

  /**
   * Get video state for a specific player
   */
  function getPlayerVideoState(playerId: string): VideoState {
    return (
      playerVideoStates.value.get(playerId) || {
        video_enabled: false,
        audio_enabled: false,
      }
    )
  }

  /**
   * Get connection state for a specific player
   */
  function getConnectionState(playerId: string): RTCPeerConnectionState | null {
    return connectionStates.value.get(playerId) || null
  }

  /**
   * Close all peer connections and cleanup
   */
  function closeAllConnections(): void {
    getLogger().info('closeAllConnections() called', {
      remoteStreamCount: remoteStreams.value.size,
      connectionCount: connectionStates.value.size
    })

    manager.cleanup()

    remoteStreams.value.clear()
    playerVideoStates.value.clear()
    connectionStates.value.clear()
    isInitialized.value = false
    currentPlayerId = null

    getLogger().info('All connections closed and state cleared')
  }

  // Cleanup on unmount
  onUnmounted(() => {
    closeAllConnections()
  })

  return {
    // Reactive state (readonly to prevent external mutation)
    remoteStreams: readonly(remoteStreams),
    playerVideoStates: readonly(playerVideoStates),
    connectionStates: readonly(connectionStates),
    isInitialized: readonly(isInitialized),

    // Connection management
    init,
    setLocalStream,
    updatePlayers,
    initiateConnections,
    notifyVideoStateChange,
    requestVideoStates,
    closeAllConnections,

    // Utilities
    getRemoteStream,
    getPlayerVideoState,
    getConnectionState,
  }
}
